import {Injectable} from '@angular/core';
import {UrlBuilderService} from '../../../services/url-builder.service';
import {SeoMetaService} from '../../../services/seo-meta.service';
import {PageViewsGa4ResolverService} from '../../../services/page-views-ga4-resolver.service';
import {MetaTag} from '../../../types/MetaTag';
import {DataLayerParamsPageViewGA4} from '../../../types/DataLayerParamsPageViewGA4';
import {DLPageTypeEnum} from '../../../enums/DataLayerEnums';
import {RoutesAddonsEnums, RoutesEnums, RoutesNamesEnum} from '../../../enums/RoutesEnums';
import {SearchWidgetDataItem} from '../../../types/SearchWidgetDataItem';
import {UrlTermsSymbolsEnum} from '../../../enums/SeoEnums';
import {AppConfigService} from '../../../services/app-config.service';

@Injectable({
  providedIn: 'root',
})
export class RelatedService {
  constructor(
    private urlBuilder: UrlBuilderService,
    private metaService: SeoMetaService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private appConfigService: AppConfigService,
  ) {}

  public trackPageView(type: RoutesAddonsEnums, typeName: string, item: SearchWidgetDataItem): void {
    const customGa4Params: DataLayerParamsPageViewGA4 = {
      pageType: DLPageTypeEnum.SERP_RELATED_PAGE,
      pageTitle: this.getPageTitle(typeName, item),
      mainCareProviderTypeId: type === RoutesAddonsEnums.RELATED_CAREPROVIDER && item ? item.id : null,
      inferredCategoryTypeId: type === RoutesAddonsEnums.RELATED_CAREPROVIDER && item ? item.id : null,
      careServiceTypeId: type === RoutesAddonsEnums.RELATED_CAREPROVIDER && item ? item.id : null,
      symptomId: type === RoutesAddonsEnums.RELATED_CAREPROVIDER && item ? item.id : null,
    };
    this.pageViewGa4Resolver.trackPageView(customGa4Params);
  }

  public updateMetaTags(
    type: RoutesAddonsEnums,
    typeName: string,
    item: SearchWidgetDataItem,
    suffix: UrlTermsSymbolsEnum,
  ): void {
    const metaTags: MetaTag[] = [];
    const pageTitle: string = this.getPageTitle(typeName, item);
    const description: string = this.createMetaDescription(typeName, item);

    metaTags.push({
      value: pageTitle,
      type: 'title',
      field: 'title',
    });

    metaTags.push({
      value: pageTitle,
      type: 'property',
      field: 'og:title',
    });

    metaTags.push({
      value: 'website',
      type: 'property',
      field: 'og:type',
    });

    metaTags.push({
      value: this.getPageUrl(type, item, suffix),
      type: 'property',
      field: 'og:url',
    });

    metaTags.push({
      value: description,
      type: 'name',
      field: 'description',
    });

    metaTags.push({
      value: description,
      type: 'property',
      field: 'og:description',
    });

    metaTags.push({
      value: this.appConfigService.appConfig.siteName,
      type: 'property',
      field: 'og:site_name',
    });

    this.metaService.updateMetaTags(metaTags);
  }

  private getPageTitle(typeName: string, item: SearchWidgetDataItem): string {
    if (typeName === RoutesNamesEnum.RELATED_CAREPROVIDER && !item) {
      return `Hitta Vårdcentral, Tandläkare, Hudläkare, Psykolog, Naprapat och mer – Jämför & Boka`;
    }
    return `Relaterade sökningar på ${item ? item.name : typeName}`;
  }

  private getPageUrl(type: RoutesAddonsEnums, item: SearchWidgetDataItem, suffix: UrlTermsSymbolsEnum): string {
    const routerLink: string[] = ['/', RoutesEnums.RELATED, type];
    if (item) {
      routerLink.push(`${item.urlPath}-${suffix}`);
    }
    return this.urlBuilder.CreateStaticLink(routerLink);
  }

  private createMetaDescription(typeName: string, item: SearchWidgetDataItem): string {
    if (typeName === RoutesNamesEnum.RELATED_CAREPROVIDER && !item) {
      return (
        'Jämför och boka de bästa legitimerade vårdgivarna såsom vårdcentraler, tandläkare, ' +
        'psykologer, naprapater, kiropraktorer, hudläkare och ögonläkare. Se omdömen och betyg, ' +
        'lista dig på en vårdcentral nära dig.'
      );
    }

    const name: string = item ? item.name : typeName;
    return `Utforska relaterade sökningar för ${name} och hitta den bästa vården för dina behov. Få mer information och rekommendationer hos oss.`;
  }
}
